import { uuid25 } from "../../../uuid/dist/uuid25.js";
import { DictionaryKind } from "./actions.js";
import { isListed } from "./membership.js";
import { toDomainTypeActions } from "./domainTypes.js";
import { findDomain, getDomainNames, getTermNames, getWordIndex, normalizeDictionaryName, orderedDictionaryRows } from "./dictionaryIndex.js";
import { addDictionaryAction, dictionaryEntries, dictionaryIds, selectDictionaryRow, updateDictionaryAction } from "./atom.actions.js";
import { composeName } from "./derive.js";
import { placeOrderKeys } from "./orderKeys.js";
import { isString } from "es-toolkit";
//#region src/engine/modules/dictionary/entryWrites.ts
var DictionaryRefusalCode = {
	duplicateName: "duplicateName",
	unknownDomain: "unknownDomain"
};
var NAMES = {
	[DictionaryKind.word]: getWordIndex,
	[DictionaryKind.term]: getTermNames,
	[DictionaryKind.domain]: getDomainNames
};
var valuesOf = (kind, values) => Object.fromEntries(dictionaryEntries(kind, values));
var isNameWritten = ({ existing, values }) => isString(values.name) && (!existing || existing.name !== values.name);
var nameOf = ({ existing, values }) => normalizeDictionaryName(values.name ?? existing?.name ?? "");
function resolveChanges(state, kind, changes) {
	const ids = dictionaryIds(state, kind);
	const rows = [];
	changes.forEach(({ id, values }, index) => {
		if (id === void 0) {
			rows.push({
				index,
				existing: null,
				values: valuesOf(kind, values)
			});
			return;
		}
		if (!isListed(ids, id)) return;
		const existing = selectDictionaryRow(state, kind, id);
		if (existing) rows.push({
			index,
			existing,
			values: valuesOf(kind, values)
		});
	});
	return rows;
}
function commitNamesOf(rows) {
	const renamed = /* @__PURE__ */ new Set();
	const counts = /* @__PURE__ */ new Map();
	for (const row of rows) {
		if (!isNameWritten(row)) continue;
		if (row.existing) renamed.add(row.existing.id);
		const name = nameOf(row);
		if (name) counts.set(name, (counts.get(name) ?? 0) + 1);
	}
	return {
		renamed,
		counts
	};
}
function refusalsOf(state, kind, row, names) {
	const refusals = [];
	const name = nameOf(row);
	const refuse = (code, value) => refusals.push({
		code,
		index: row.index,
		name,
		value
	});
	if (name && isNameWritten(row)) {
		if ((NAMES[kind](state).byName.get(name) ?? []).filter(({ id }) => id !== row.existing?.id && !names.renamed.has(id)).length || (names.counts.get(name) ?? 0) > 1) refuse(DictionaryRefusalCode.duplicateName, name);
	}
	const domainId = row.values.domainId;
	if (kind === DictionaryKind.term && domainId && domainId !== Reflect.get(row.existing ?? {}, "domainId") && !isListed(state.doc.domainIds, domainId)) refuse(DictionaryRefusalCode.unknownDomain, domainId);
	return refusals;
}
function toEntryWrites(state, kind, row, names) {
	const values = { ...row.values };
	const { existing } = row;
	if (kind === DictionaryKind.term && isString(values.name)) {
		const composed = (values.physicalName ?? Reflect.get(existing ?? {}, "physicalName") ?? "").trim() ? null : composeName(state, values.name);
		if (composed) values.physicalName = composed;
	}
	const refusals = names ? refusalsOf(state, kind, {
		...row,
		values
	}, names) : [];
	const changed = Object.fromEntries(Object.entries(values).filter(([field, value]) => existing ? Reflect.get(existing, field) !== value : value !== ""));
	const hasChanges = Object.keys(changed).length > 0;
	if (!existing) return {
		action: addDictionaryAction({
			kind,
			id: uuid25(),
			...hasChanges ? { values: changed } : {}
		}),
		refusals,
		domainType: null
	};
	return {
		action: hasChanges ? updateDictionaryAction({
			kind,
			id: existing.id,
			values: changed
		}) : null,
		refusals,
		domainType: kind === DictionaryKind.domain && isString(changed.dataType) ? {
			id: existing.id,
			dataType: changed.dataType
		} : null
	};
}
function placeNewRows(state, kind, rows) {
	const unplaced = rows.filter(({ existing, values }) => !existing && !values.order);
	if (!unplaced.length) return [];
	const sheet = orderedDictionaryRows(state, kind);
	const { keys, rekeyed } = placeOrderKeys(sheet, sheet.length, unplaced.length);
	unplaced.forEach(({ values }, index) => {
		values.order = keys[index];
	});
	return rekeyed;
}
var rekeyActions = (kind, rekeyed) => rekeyed.map(({ id, order }) => updateDictionaryAction({
	kind,
	id,
	values: { order }
}));
function toWriteActions(state, writes) {
	const domainTypes = /* @__PURE__ */ new Map();
	const actions = [];
	for (const { action, domainType } of writes) {
		if (action) actions.push(action);
		if (domainType) domainTypes.set(domainType.id, domainType.dataType);
	}
	if (domainTypes.size) actions.push(...toDomainTypeActions(state, { domainTypes }));
	return actions;
}
function validateDictionaryValues(state, kind, changes) {
	const rows = resolveChanges(state, kind, changes);
	const names = commitNamesOf(rows);
	return rows.flatMap((row) => refusalsOf(state, kind, row, names));
}
function toDictionaryCommit(state, kind, changes) {
	const rows = resolveChanges(state, kind, changes);
	const rekeyed = placeNewRows(state, kind, rows);
	const names = commitNamesOf(rows);
	const writes = rows.map((row) => toEntryWrites(state, kind, row, names));
	const refusals = writes.flatMap(({ refusals }) => refusals);
	return {
		actions: refusals.length ? [] : [...rekeyActions(kind, rekeyed), ...toWriteActions(state, writes)],
		refusals
	};
}
function toDictionaryImport(state, kind, rows) {
	const folded = /* @__PURE__ */ new Map();
	for (const row of rows) {
		const name = normalizeDictionaryName(row.name);
		if (name) folded.set(name, row);
	}
	const names = NAMES[kind](state);
	const unknownDomains = /* @__PURE__ */ new Map();
	const entries = [];
	for (const [name, row] of folded) {
		const values = valuesOf(kind, row);
		const domain = Reflect.get(row, "domain");
		if (kind === DictionaryKind.term && isString(domain)) {
			const key = normalizeDictionaryName(domain);
			const found = key ? findDomain(state, key) : void 0;
			values.domainId = found?.id ?? "";
			if (key && !found && !unknownDomains.has(key)) unknownDomains.set(key, domain.trim());
		}
		const existing = names.byName.get(name)?.[0] ?? null;
		entries.push({
			index: 0,
			existing,
			values
		});
	}
	const rekeyed = placeNewRows(state, kind, entries);
	const writes = [];
	let added = 0;
	let updated = 0;
	for (const entry of entries) {
		const write = toEntryWrites(state, kind, entry);
		if (write.action) {
			writes.push(write);
			if (entry.existing) updated++;
			else added++;
		}
	}
	return {
		actions: [...rekeyActions(kind, rekeyed), ...toWriteActions(state, writes)],
		added,
		updated,
		folded: rows.length - folded.size,
		unknownDomains: [...unknownDomains.values()]
	};
}
//#endregion
export { DictionaryRefusalCode, toDictionaryCommit, toDictionaryImport, validateDictionaryValues };
